import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import './Auth.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-split">
      {/* Left panel - branded visual */}
      <div className="auth-brand-panel">
        <div className="auth-brand-content">
          <Link to="/" className="auth-brand-logo">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <rect width="48" height="48" rx="12" fill="rgba(255,255,255,0.15)" />
              <path d="M14 28c0-5.523 4.477-10 10-10h0c5.523 0 10 4.477 10 10" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" fill="none" />
              <circle cx="24" cy="28" r="3" fill="#fff" />
              <path d="M16 22V18a8 8 0 0 1 16 0v4" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" fill="none" />
              <path d="M12 32h24" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M15 35h18" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </Link>
          <h1 className="auth-brand-title">CloudViewMSP</h1>
          <p className="auth-brand-tagline">Your single source of truth for MSP billing reconciliation</p>

          <div className="auth-brand-features">
            <div className="auth-brand-feature">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" fill="rgba(255,255,255,0.9)"/>
              </svg>
              <span>Unified dashboard across all vendor platforms</span>
            </div>
            <div className="auth-brand-feature">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" fill="rgba(255,255,255,0.9)"/>
              </svg>
              <span>Automated license reconciliation</span>
            </div>
            <div className="auth-brand-feature">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" fill="rgba(255,255,255,0.9)"/>
              </svg>
              <span>Spot billing discrepancies instantly</span>
            </div>
            <div className="auth-brand-feature">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" fill="rgba(255,255,255,0.9)"/>
              </svg>
              <span>Many integrations — custom APIs on request</span>
            </div>
          </div>

          <div className="auth-brand-decoration">
            <svg width="100%" height="120" viewBox="0 0 400 120" fill="none" preserveAspectRatio="none">
              <path d="M0 60C50 20 100 80 150 50C200 20 250 90 300 60C350 30 400 70 400 70V120H0V60Z" fill="rgba(255,255,255,0.05)" />
              <path d="M0 80C60 50 120 90 180 70C240 50 300 100 400 80" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" fill="none" />
            </svg>
          </div>
        </div>
      </div>

      {/* Right panel - login form */}
      <div className="auth-form-panel">
        <div className="auth-form-wrapper">
          <div className="auth-form-header">
            <h2 className="auth-form-title">Welcome back</h2>
            <p className="auth-form-subtitle">Sign in to your account to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label className="label">Email address</label>
              <div className="input-wrapper">
                <svg className="input-icon" width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" fill="currentColor"/>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" fill="currentColor"/>
                </svg>
                <input
                  type="email"
                  className="input input-with-icon"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@company.com"
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="label">Password</label>
              <div className="input-wrapper">
                <svg className="input-icon" width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" fill="currentColor"/>
                </svg>
                <input
                  type="password"
                  className="input input-with-icon"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block btn-lg"
              disabled={loading}
            >
              {loading ? (
                <span className="btn-loading">
                  <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                    <path d="M12 2a10 10 0 019.95 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                  Signing in...
                </span>
              ) : 'Sign in'}
            </button>
          </form>

          <p className="auth-back-link">
            Need an account? Ask a CloudViewMSP administrator to create one for you.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
